import { Component, computed, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { TitleCasePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { signOut } from 'aws-amplify/auth';
import { NavigationService, View } from '../../../services/navigation.service';
import { StorageService } from '../../../services/storage.service';
import { AppStateService } from '../../core/services/app-state.service';
import { ThemeService, ACCENTS } from '../../core/services/theme.service';
import { MailboxService } from '../../../services/mailbox.service';
import { MailService } from '../../../services/mail.service';
import { WebsocketService } from '../../../services/websocket.service';
import { EtichetteMessaggioService } from '../../../services/etichette-messaggio.service';
import { Mailbox } from '../../../models/mailbox.model';
import {
  EtichettaMessaggioRTO,
  EtichettaTipo,
} from '../../../models/etichetta-messaggio.model';

@Component({
  selector: 'pec-sidebar',
  standalone: true,
  imports: [
    TitleCasePipe,
    MatIconModule,
    MatTooltipModule,
    MatMenuModule,
    MatDividerModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly websocket = inject(WebsocketService);
  navigationService = inject(NavigationService);
  storageService = inject(StorageService);
  state = inject(AppStateService);
  theme = inject(ThemeService);
  mailboxService = inject(MailboxService);
  mailService = inject(MailService);
  etichetteService = inject(EtichetteMessaggioService);

  sidebarItems = this.navigationService.sidebarItems;
  footerNavItems = this.navigationService.footerNavItems;
  currentView = this.navigationService.currentView;
  readonly accentNames = Object.keys(ACCENTS);
  readonly syncingMailboxes = signal<Set<string>>(new Set());
  /** Sezione di etichette associata a ciascuna voce di navigazione che le espone. */
  private readonly etichettaTipoPerView: Partial<Record<View, EtichettaTipo>> = {
    archive: 'ARCHIVED',
  };

  /**
   * Etichette già raggruppate per sezione: il filtro viene calcolato una volta per ogni
   * variazione dello store, non a ogni ciclo di change detection della sidebar.
   */
  private readonly etichettePerTipo = computed<Record<EtichettaTipo, EtichettaMessaggioRTO[]>>(
    () => {
      const all = this.etichetteService.etichette();
      return {
        INBOX: all.filter(e => e.tipo === 'INBOX'),
        ARCHIVED: all.filter(e => e.tipo === 'ARCHIVED'),
      };
    },
  );

  /** Primo segmento della rotta corrente: determina l'unica voce attiva in sidebar. */
  private readonly activeRoute = signal<string>('');

  /** Sezioni che supportano il filtro per casella monitorata. */
  private readonly filterableViews: string[] = ['inbox', 'sent', 'archive'];

  private routerSubscription?: Subscription;
  private connectionSubscription?: Subscription;
  /** Un'iscrizione a /topic/casella/{id}/sync per ogni casella visibile all'utente. */
  private readonly iscrizioniSync = new Map<number, Subscription>();
  private wsConnesso = false;
  // Allinea le iscrizioni quando cambia l'elenco delle caselle; alla (ri)connessione ci pensa ngOnInit.
  private readonly allineaSyncEffect = effect(() => {
    const caselle = this.mailboxService.mailboxes();
    if (this.wsConnesso) this.allineaIscrizioniSync(caselle);
  });

  private static readonly WIDTH_KEY = 'pec-sidebar-width';
  private static readonly DEFAULT_WIDTH = 248;

  readonly width = signal<number>(this.readStoredWidth());

  ngOnInit(): void {
    // La sidebar può essere creata dopo la navigazione iniziale: allinea subito lo stato attivo.
    this.applyActiveRoute(this.router.url);

    // Le etichette alimentano le sotto-voci di Archivio; lo store è condiviso col dialog di
    // selezione, quindi una nuova etichetta creata lì compare qui senza altre chiamate.
    this.etichetteService.fetchAll();

    this.routerSubscription = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(e => this.applyActiveRoute(e.urlAfterRedirects));

    this.connectionSubscription = this.websocket.isConnected$.subscribe(isConnected => {
      this.wsConnesso = isConnected;
      if (!isConnected) return;
      // Nuova connessione: le iscrizioni precedenti appartenevano alla sessione chiusa.
      this.chiudiIscrizioniSync();
      this.allineaIscrizioniSync(this.mailboxService.mailboxes());
    });
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    this.connectionSubscription?.unsubscribe();
    this.chiudiIscrizioniSync();
  }

  /** Stato di sincronizzazione: un topic per casella, ricevuto solo dagli utenti abilitati. */
  private allineaIscrizioniSync(caselle: Mailbox[]) {
    const ids = new Set(caselle.map(c => c.id));
    for (const [id, sub] of this.iscrizioniSync) {
      if (!ids.has(id)) {
        sub.unsubscribe();
        this.iscrizioniSync.delete(id);
      }
    }
    for (const casella of caselle) {
      if (casella.id == null || this.iscrizioniSync.has(casella.id)) continue;
      this.iscrizioniSync.set(
        casella.id,
        this.websocket.subscribe(`/topic/casella/${casella.id}/sync`).subscribe({
          next: (value: { email: string; stato: string }) => this.setMailboxSyncStatus(value.email, value.stato),
          error: err => console.log(err),
        }),
      );
    }
  }

  private chiudiIscrizioniSync() {
    this.iscrizioniSync.forEach(sub => sub.unsubscribe());
    this.iscrizioniSync.clear();
  }

  handleRoute(item: { route?: string; id: View }) {
    if (item.route) {
      this.router.navigate(['/' + item.route]);
    }
  }

  private applyActiveRoute(url: string) {
    const category = this.navigationService.getCategoryForRoute(url);
    if (category) {
      this.navigationService.setMainCategory(category);
    }

    // Primo segmento della URL (es. '/inbox/detail?id=1' → 'inbox'): confronto esatto, così
    // resta attiva una sola voce anche quando la rotta non è tra quelle di navigazione.
    const segment = this.router.parseUrl(url).root.children['primary']?.segments[0]?.path ?? '';
    this.activeRoute.set(segment);

    const matched = this.sidebarItems().find(item => item.route === segment);
    if (matched) {
      this.currentView.set(matched.id);
    }
  }

  /**
   * Seleziona la casella e resta nella sezione filtrabile corrente (inbox/inviati/archivio),
   * ripiegando su Inbox se ci si trova altrove. Il filtro viene applicato dalla pagina di
   * destinazione, che reagisce al cambio di `casellaMonitorata`.
   */
  navigateToMailbox(mailbox: Mailbox) {
    const alreadySelected = this.mailboxService.casellaMonitorata()?.email === mailbox.email;
    this.mailboxService.setCasellaMonitorata(alreadySelected ? null : mailbox);

    const current = this.activeRoute();
    const target = this.filterableViews.includes(current) ? current : 'inbox';
    this.router.navigate(['/' + target]);
  }

  isMailboxSelected(email: string): boolean {
    return this.mailboxService.casellaMonitorata()?.email === email;
  }

  isEtichettaSelected(id: number): boolean {
    return this.etichetteService.etichettaSelezionata()?.id === id;
  }

  /** True se la voce di navigazione espone le etichette come sotto-voci. */
  hasEtichette(id: View): boolean {
    return !!this.etichettaTipoPerView[id];
  }

  /** Etichette della sezione corrispondente alla voce di navigazione. */
  etichetteFor(id: View): EtichettaMessaggioRTO[] {
    const tipo = this.etichettaTipoPerView[id];
    return tipo ? this.etichettePerTipo()[tipo] : [];
  }

  /**
   * Le sotto-voci si comportano come un filtro a interruttore: un secondo clic sull'etichetta
   * attiva la deseleziona. Il filtro viene applicato dalla pagina di destinazione, che reagisce
   * al cambio di `etichettaSelezionata` ricaricando la prima pagina.
   */
  selectEtichetta(etichetta: EtichettaMessaggioRTO) {
    this.etichetteService.toggleEtichettaSelezionata(etichetta);
    this.router.navigate([etichetta.tipo === 'ARCHIVED' ? '/archive' : '/inbox']);
  }

  private setMailboxSyncStatus(email: string, stato: string) {
    this.syncingMailboxes.update(current => {
      const next = new Set(current);
      if (stato === 'IN_PROGRESS') {
        next.add(email);
      } else {
        next.delete(email);
      }
      return next;
    });
  }

  isMailboxSyncing(email: string): boolean {
    return this.syncingMailboxes().has(email);
  }

  isViewActive(item: { route?: string; id: View }): boolean {
    return !!item.route && this.activeRoute() === item.route;
  }

  getUser() {
    return this.storageService.getUser() as { user: string; ruolo: string; email: string } | null;
  }

  getUserInitials(): string {
    const user = this.getUser();
    if (!user?.user) return '?';
    return user.user.slice(0, 2).toUpperCase();
  }

  async doLogout() {
    this.storageService.clearUser();
    await signOut();
  }

  getIcon(iconName: string): SafeHtml {
    const icons: Record<string, string> = {
      inbox: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 13l3-8h12l3 8"/><path d="M3 13v6h18v-6"/><path d="M3 13h5l1 2h6l1-2h5"/></svg>`,
      send: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4l17 8-17 8 3-8-3-8z"/><path d="M7 12h14"/></svg>`,
      archive: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 5h18v4H3z"/><path d="M5 9v10h14V9"/><path d="M10 13h4"/></svg>`,
      edit: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 4h11l4 4v12H5z"/><path d="M8 10h8M8 14h8M8 18h5"/></svg>`,
      shield: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v6M12 16v.5"/></svg>`,
      'book-open': `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="9" r="3.5"/><path d="M5 20c1.5-3.5 4-5 7-5s5.5 1.5 7 5"/></svg>`,
      mail: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m2 7 10 7 10-7"/></svg>`,
      cog: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1"/></svg>`,
      support: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M9.5 9.5a2.5 2.5 0 115 0c0 1.5-2.5 2-2.5 3.5M12 17v.5"/></svg>`,
    };
    return this.sanitizer.bypassSecurityTrustHtml(icons[iconName] ?? '');
  }

  startResize(ev: PointerEvent): void {
    ev.preventDefault();
    const handle = ev.currentTarget as HTMLElement;
    handle.setPointerCapture(ev.pointerId);

    const onMove = (e: PointerEvent) => this.width.set(this.clamp(e.clientX));
    const onUp = () => {
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      this.storeWidth();
    };
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
  }

  resetWidth(): void {
    this.width.set(this.clamp(SidebarComponent.DEFAULT_WIDTH));
    this.storeWidth();
  }

  /** La sidebar parte dal bordo sinistro: la x del puntatore è la nuova larghezza. */
  private clamp(px: number): number {
    const vw = window.innerWidth;
    return Math.min(Math.max(px, vw * 0.1), vw * 0.2);
  }

  private readStoredWidth(): number {
    try {
      const v = Number(localStorage.getItem(SidebarComponent.WIDTH_KEY));
      if (v > 0) return this.clamp(v);
    } catch {}
    return this.clamp(SidebarComponent.DEFAULT_WIDTH);
  }

  private storeWidth(): void {
    try { localStorage.setItem(SidebarComponent.WIDTH_KEY, String(Math.round(this.width()))); } catch {}
  }
}
